import { useContext, useMemo, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { ApprovalDetails, DatasetStatus, PendingStatus } from '@components'
import { submissionContext } from '@contexts'
import { CHANNELS, getPusherInstance } from '@global/js/pusher'
import { Submission, SubmissionSummary } from '@global/types'
import { SubmissionStatusStepper, SubmissionStatusRatio } from '@submissions/submissions/components'
import { Phase, phaseProgressMap, steps } from '@submissions/submissions/config'
import { SubmissionStatusOutbound } from '@submissions/submissions/submission-status-outbound'
import { useApprovalFlowDetails } from '@submissions/submissions/utils'
import * as css from './styles'
import { t } from '@lingui/core/macro'
import { RootState } from '@redux/store'

interface Props {
  submission: Submission
  requireApproval?: boolean
  creativeClassificationEnabled?: boolean
}

export const DatasetTracker = ({
  submission,
  requireApproval,
  creativeClassificationEnabled,
}: Props) => {
  const Client = getPusherInstance()
  const { id: submissionId, dataset_id: datasetId } = submission
  const accountId = useSelector((state: RootState) => state.Auth.account.id)
  const userId = useSelector((state: RootState) => state.Auth.user?.id)
  const { fetchSubmissionSummaries, submissionSummaryById, fetchSubmissionByDatasetId } =
    useContext(submissionContext)
  const summary: SubmissionSummary | null = useMemo(
    () => submissionSummaryById[submissionId],
    [submissionSummaryById, submissionId],
  )
  const approvalStatus = submission?.approval_result?.approval_status || submission?.status
  const approvalFlowEnabled = Boolean(
    approvalStatus === PendingStatus.AWAITING_APPROVAL ||
      approvalStatus === PendingStatus.REJECTED ||
      approvalStatus === DatasetStatus.APPROVED ||
      requireApproval,
  )
  const { approvalLabel, approvalActionTimestamp, validatingPhaseLabelForApprovalStep } =
    useApprovalFlowDetails(approvalFlowEnabled, submission)

  const subscribeToSummaryChanges = useCallback(() => {
    if (submissionId) {
      const channelDefinition = CHANNELS.ACCOUNT_DATASET_SUBMISSION_CHANGES
      const channel = Client.subscribe(channelDefinition.name(accountId, userId))
      channel.bind(
        channelDefinition.events.DATASET_SUBMISSION_PHASE_CHANGED,
        payload => {
          if (payload.datasetSubmissionId === submissionId) {
            fetchSubmissionByDatasetId(payload.datasetId)
            fetchSubmissionSummaries([payload.datasetSubmissionId])
          }
        },
        'dataset-tracker',
      )
    }
  }, [accountId, userId, submissionId])

  const unsubscribeToSummaryChanges = useCallback(() => {
    const channelDefinition = CHANNELS.ACCOUNT_DATASET_SUBMISSION_CHANGES
    const channelName = channelDefinition.name(accountId, userId)
    const channel = Client.channel(channelName)
    if (channel) {
      Client.unsubscribe(channelName)
      channel.unbind(
        channelDefinition.events.DATASET_SUBMISSION_PHASE_CHANGED,
        null,
        'dataset-tracker',
      )
    }
  }, [accountId, userId])

  useEffect(() => {
    fetchSubmissionByDatasetId(datasetId)
    fetchSubmissionSummaries([submissionId])
    subscribeToSummaryChanges()

    return unsubscribeToSummaryChanges
  }, [datasetId, submissionId])

  const hasOutbound = Boolean(summary?.datasetSubmissionId)
  const showOutbound =
    hasOutbound &&
    (submission.phase === Phase.WAITING_ON_OUTSTREAMS || submission.phase === Phase.COMPLETED)

  const submissionSteps = steps(validatingPhaseLabelForApprovalStep, creativeClassificationEnabled)

  return (
    <>
      <css.PhasesContainer>
        <SubmissionStatusRatio
          phase={submission.phase}
          steps={submissionSteps}
          progressMap={phaseProgressMap}
        />
        <css.VerticalDivider />
        <SubmissionStatusStepper
          submission={submission}
          steps={submissionSteps}
          progressMap={phaseProgressMap}
          creativeClassificationEnabled={creativeClassificationEnabled}
        />
      </css.PhasesContainer>
      {Boolean(approvalActionTimestamp) && submission?.approval_result && (
        <css.ApprovalActionContainer>
          <ApprovalDetails approvalResult={submission?.approval_result} label={approvalLabel} />
        </css.ApprovalActionContainer>
      )}
      {showOutbound && (
        <>
          <css.HorizontalDivider />
          <css.SectionHeader>{t`Outbound destinations`}</css.SectionHeader>
          <css.OutboundContainer css={css.padding}>
            <SubmissionStatusOutbound submission={submission} submissionSummary={summary} />
          </css.OutboundContainer>
        </>
      )}
    </>
  )
}
